import React, { ReactElement } from 'react';

import { ButtonAPI, DeckAPI, FetchAPI, ModalService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSelectItem, OnUnselectItem } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent, RenderEvent, SelectItemEvent, UnselectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { Inject } from '@odinjs/odin';

import moment from 'moment/moment.js'
import BaseController from '../BaseController';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import RescheduleContext, { rescheduleCancelModal } from '../../utils/reschedule-utils';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ScheduleType } from '../../constants/schedule-type';
import { APPOINTMENT_TYPE } from '../../constants/appointment-type';
import UserContext from '../../utils/user-context';
import { FragmentNames, RescheduleTransferenceModalAliases } from '../../codegen';
import { YES } from '../../constants/application-constants';

import './appointments-reschedule-transference-controller.css';

interface Schedule {
  dsConvenio?: string;
  dsEnderecoEstab?: string;
  dsStatus?: string;
  dsTitulo?: string;
  dsTypeSchedule?: string;
  dtAgendamento?: string;
  hrAgendamento?: string;
  ieScheduleType: string;
  ieStatus?: string;
  nrSequencia: number;
}

@Controller({
  component: RescheduleTransferenceModalAliases.LAYOUT_RESCHEDULE_TRANSFERENCE_MODAL,
  fragment: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL
})
export default class AppointmentsRescheduleTransferenceController extends BaseController {

  private schedulesSelected: Schedule[] = [];

  @Inject(RescheduleTransferenceModalAliases.RESCHEDULE_CONFIRM_BUTTON)
    confirmButtonApi!: ButtonAPI;

  @Inject(RescheduleTransferenceModalAliases.FETCH_APPOINTMENTS)
    fetchListApi!: FetchAPI;

  @Inject(RescheduleTransferenceModalAliases.DECK_APPOINTMENTS)
    deckAppointmentsApi!: DeckAPI;

  fetchDataList(): void {
    const { reagendarConsulta, reagendarExame, reagendarServico, rescheduleCheckup } = RescheduleContext.rescheduleParameters;
    const initialDate = moment().format(ISO_DATE_FORMAT);
    const filter = [];

    if (rescheduleCheckup === YES) {
      filter.push(APPOINTMENT_TYPE.CHECKUP);
    }

    if (reagendarConsulta === YES) {
      filter.push(APPOINTMENT_TYPE.CONSULTATIONS);
    }

    if (reagendarExame === YES) {
      filter.push(APPOINTMENT_TYPE.EXAMS);
    }

    if (reagendarServico === YES) {
      filter.push(APPOINTMENT_TYPE.SERVICES);
    }

    this.deckAppointmentsApi.setData([]);

    this.fetchListApi.setParameters({
      filter,
      initialDate,
    });
  }

  @OnMount(RescheduleTransferenceModalAliases.LAYOUT_RESCHEDULE)
  onMountLayoutAppointments(): void {
    this.fetchDataList();
  }

  @OnAfterFetch(RescheduleTransferenceModalAliases.FETCH_APPOINTMENTS)
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as Record<string, Value>[];
    if (data) {
      const routeParams = this.historyService.getCurrentRouteParams();
      const selectedSchedule = data
        .find(item => String(item.nrSequencia) === routeParams.nrSeqAgendaConsulta || String(item.nrSequencia) === routeParams.nrSeqAgendaExame);
      if (selectedSchedule) {
        const selectedScheduleData = {
          ieScheduleType: selectedSchedule.ieScheduleType,
          nrSequencia: selectedSchedule.nrSequencia,
        };
        this.schedulesSelected.push(selectedScheduleData as Schedule);
        this.confirmButtonApi.setEnabled(true);
        event.data = { ...data };
        this.deckAppointmentsApi.setData(data);
      }
    }
  }

  @OnMount(RescheduleTransferenceModalAliases.DECK_APPOINTMENTS)
  onDeckMount(): void {
    const routeParams = this.historyService.getCurrentRouteParams();
    this.deckAppointmentsApi.selectCard(String(routeParams.nrSeqAgendaConsulta || routeParams.nrSeqAgendaExame || ''));
    RescheduleContext.hasInsuranceSelected = true;
  }

  @OnSelectItem(RescheduleTransferenceModalAliases.DECK_APPOINTMENTS)
  onCardDeckSelect(event: SelectItemEvent<Schedule>): void {
    const hasItemBeenAdded = this.schedulesSelected
      .some(item => item.nrSequencia === event.item.nrSequencia);

    if (!hasItemBeenAdded) {
      this.schedulesSelected.push(event.item);
    }

    if (this.schedulesSelected.length > 0) {
      RescheduleContext.hasInsuranceSelected = true;
      this.confirmButtonApi.setEnabled(true);
    }
  }

  @OnUnselectItem(RescheduleTransferenceModalAliases.DECK_APPOINTMENTS)
  onCardDeckUnselect(event: UnselectItemEvent<Schedule>): void {
    const ScheduleIndex = this.schedulesSelected
      .findIndex(item => item.nrSequencia === event.item.nrSequencia);

    this.schedulesSelected.splice(ScheduleIndex, 1)

    if (this.schedulesSelected.length === 0) {
      RescheduleContext.hasInsuranceSelected = false;
      this.confirmButtonApi.setEnabled(false);
    }
  }

  @OnClick(RescheduleTransferenceModalAliases.RESCHEDULE_CONFIRM_BUTTON)
  onClickConfirmButton(): void {
    const consultList = this.schedulesSelected.filter(item => item.ieScheduleType === ScheduleType.CONSULTATION || item.ieScheduleType === ScheduleType.SERVICE);
    const consultListP = consultList.length > 0 ? consultList.map(item => item.nrSequencia) + ',' : '';
    const examList = this.schedulesSelected.filter(item => item.ieScheduleType === ScheduleType.EXAM);
    const examListP = examList.length > 0 ? examList.map(item => item.nrSequencia) + ',': '';

    const body = { listaConsultaP: consultListP, listaExameP: examListP };

    HttpService.post(
      APPLICATION_ENDPOINTS.RESCHEDULE_ITEMS_TRANSFERENCE, { patientId: UserContext.patientId }, { body }
    ).then(response => {
      RescheduleContext.nrSeqAgeint = response.body as number;
      this.historyService.push(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
      ModalService.close('reschedule-transference-modal');
      RescheduleContext.hasInsuranceSelected = false;
    });
  }

  @OnClick(RescheduleTransferenceModalAliases.RESCHEDULE_CANCEL_BUTTON)
  onClickCancelButton(): void {
    if (RescheduleContext.hasInsuranceSelected) {
      ModalService.openDialog(rescheduleCancelModal);
    } else {
      ModalService.close('reschedule-transference-modal');
    }
  }

  @OnDynamicContentRender(RescheduleTransferenceModalAliases.DECK_APPOINTMENTS)
  onDeckGroupRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const data = moment(event.entry);
      const dateFormat = data.format('DD MMMM');
      const today = moment.utc().startOf('day');
      const dif = today.diff(data, 'days') ;
      const indicateDay = dif !== 0 ? data.fromNow() : data.calendar().split(' ')[0];
      const renderSuggestionDate: () => ReactElement = () => {
        return (
          <div className='deck-title-appointments-rechedule-transference-controller'  >
            <span>{`${dateFormat} (${indicateDay})`}</span>
          </div>
        );
      }
      render(renderSuggestionDate);
    }
  }
}
